import type { ProfileResponse, ProfileSort } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

/** Rows per page, matching the original `useInfiniteApi('/profiles', 25)` default. */
export const DEFAULT_PAGE_SIZE = 25;

/** Bulk-deactivate lifecycle (`error` holds the failure snackbar message). */
export type DeactivateStatus = 'idle' | 'deactivating' | 'succeeded' | 'failed';

export interface DeactivateState {
  status: DeactivateStatus;
  /** Rows deactivated (drives the success snackbar count). */
  count: number;
  error: string | null;
}

/**
 * Redux state for the admin Profiles list. Server-side pagination + filtering: the
 * slice holds the current page's rows, the grand `total`, the load lifecycle
 * (`error` is a banner message), the current page / pageSize (`page` is 0-indexed),
 * the pruned `filters` and the `sort` (null = by name, #714) that drive the fetch,
 * and the bulk-deactivate lifecycle.
 * Filter-bar edit state, column config and row selection stay local to the page
 * (view state); every field here is serialisable.
 */
export interface ProfilesState {
  rows: ProfileResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  filters: FilterGroup | null;
  sort: ProfileSort | null;
  deactivate: DeactivateState;
}

export const emptyDeactivate: DeactivateState = { status: 'idle', count: 0, error: null };

export const initialState: ProfilesState = {
  rows: [],
  total: 0,
  loading: false,
  error: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: null,
  sort: null,
  deactivate: emptyDeactivate,
};
